import { CompactType, Layout, LayoutItem } from '../types';

/**
 * Finds the height of a layout by finding the bottom y coordinate (considering height of layout items)
 * of the layout.
 *
 * Ignores the minimum layout item (which may not start at 0) since that's impacted by the compacting
 * in the system (i.e. 'gravity').
 *
 * @returns Approximate height (not including bias due to not starting at 0) if it exists else null.
 */
export function findHeightOfLayout(layout: Layout): number | null {
  let max = null;

  for (const { y, h } of layout) {
    // layout elements can't have a width less than 1
    const endY = y + (h > 0 ? h : 1);

    if (!max || endY > max) max = endY;
  }

  return max;
}

if (import.meta.vitest) {
  describe('findHeightOfLayout', () => {
    it('Returns null if array is empty', () => {
      expect(findHeightOfLayout([])).toEqual(null);
    });
    it('Returns 1 if all elements have 0 height since 1 is grid minimum', () => {
      expect(findHeightOfLayout([{ x: 0, y: 0, key: 'A', h: 0, w: 0 }])).toEqual(1);
    });
    it('Returns 0 if a negative height causes 0 to actually be a valid coordinate', () => {
      expect(findHeightOfLayout([{ x: 0, y: -1, key: 'A', h: 1, w: 0 }])).toEqual(0);
    });
    it('Returns the maximum if multiple layout items exist', () => {
      expect(
        findHeightOfLayout([
          { x: 0, y: -1, key: 'A', h: 1, w: 0 },
          { x: 0, y: 2, key: 'A', h: 5, w: 0 },
          { x: 0, y: 0, key: 'A', h: 0, w: 0 },
        ])
      ).toEqual(7);
    });
  });
}

export function cloneLayout(layout: Layout): Layout {
  return layout.map(cloneLayoutItem);
}

export function cloneLayoutItem(layoutItem: LayoutItem): LayoutItem {
  return {
    key: layoutItem.key,
    w: layoutItem.w,
    h: layoutItem.h,
    x: layoutItem.x,
    y: layoutItem.y,
    minW: layoutItem.minW,
    maxW: layoutItem.maxW,
    minH: layoutItem.minH,
    maxH: layoutItem.maxH,
  };
}

/**
 * Given two items determine if they collide
 */
export function checkIfLayoutItemsCollide(a: LayoutItem, b: LayoutItem): boolean {
  // We do a check to ensure that keys are unique, so we can presume
  // that if the keys on the layout items are the same they are the same item.
  return (
    a.key != b.key && // different item
    a.x + (a.w > 0 ? a.w : 1) > b.x && // a does not sit to the left of b
    b.x + (b.w > 0 ? b.w : 1) > a.x && // a does not sit to the right of b
    a.y + (a.h > 0 ? a.h : 1) > b.y && // a does not sit above b
    b.y + (b.h > 0 ? b.h : 1) > a.y // a does not sit below b
  );
}

if (import.meta.vitest) {
  describe('checkIfLayoutItemsCollide', () => {
    it('A below B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 10, h: 1, w: 1, key: 'A' }, { x: 0, y: 1, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A overlaps from below B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 2, h: 1, w: 1, key: 'A' }, { x: 0, y: 1, h: 2, w: 1, key: 'B' })
      ).toEqual(true);
    });
    it('A sits just below B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 2, h: 1, w: 1, key: 'A' }, { x: 0, y: 1, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A to the right of B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 10, y: 0, h: 1, w: 1, key: 'A' }, { x: 1, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A overlaps from the right of B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 2, y: 0, h: 1, w: 1, key: 'A' }, { x: 1, y: 0, h: 1, w: 2, key: 'B' })
      ).toEqual(true);
    });
    it('A to the right of B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 2, y: 0, h: 1, w: 1, key: 'A' }, { x: 1, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A above B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 1, h: 1, w: 1, key: 'A' }, { x: 0, y: 10, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A overlaps from above B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 1, h: 2, w: 1, key: 'A' }, { x: 0, y: 2, h: 1, w: 1, key: 'B' })
      ).toEqual(true);
    });
    it('A sits just above B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 1, h: 1, w: 1, key: 'A' }, { x: 0, y: 2, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A to the left of B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 1, y: 0, h: 1, w: 1, key: 'A' }, { x: 10, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A overlaps from the left of B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 0, h: 1, w: 2, key: 'A' }, { x: 1, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(true);
    });
    it('A to the left of B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 1, y: 0, h: 1, w: 1, key: 'A' }, { x: 2, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(false);
    });
    it('A has same coordinates as B', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 0, h: 1, w: 1, key: 'A' }, { x: 0, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(true);
    });
    it('A has same coordinates as B with one having 0 width/height', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 0, h: 0, w: 0, key: 'A' }, { x: 0, y: 0, h: 1, w: 1, key: 'B' })
      ).toEqual(true);
    });
    it('(Fast Route): Keys are the same', () => {
      expect(
        checkIfLayoutItemsCollide({ x: 0, y: 0, h: 0, w: 0, key: 'A' }, { x: 0, y: 0, h: 0, w: 0, key: 'A' })
      ).toEqual(false);
    });
  });
}

export function correctBoundsInLayout(layout: Layout, cols: number): void {
  /**
   * React/Vue grid layout handle this very differently, they seem to face a ton of bugs
   * with how they treat statics in this way... what they'll do if statics are overlapping
   * is just push one (of the two) statics downwards (vertically) until it's no longer overlapping
   *
   * React reference (Vue is practically identical): https://github.com/react-grid-layout/react-grid-layout/blob/5126e7be9a1e38053e078f2b17e1a0f3a7abcda0/lib/utils.js#L347
   *
   * This is quite logically odd for the following reasons;
   * - Developers set statics in the layout config, if they want statics to overlap that is on them
   *   it is very weird to me that a library would break statics being statics just because we get
   *   (arguably) bad layouts.  Instead I just expose a 'validateStrictLayout' that validates that
   *   static layouts don't overlap.
   * - Why always push down vertically?  Arguably to the right then down is more sensical
   * - Since they add all non statics (line 361) to the list of possible collisions this means that
   *   they could actually have statics collide with other *NON-statics* this would result in statics
   *   no longer being static for even their original purpose which is to have other items flow around them.
   *
   * Amongst this and over bugs (i.e. when it overflows to the left the width of the item becomes
   * the length of the grid) I've instead opted for the following appraoch;
   * - Simply ensure all items are within bounds and handle 0 collision checking
   * - Don't trust the output of this layout (of non statics) to not have collisions and
   *   handle collision checking after this
   */

  for (const item of layout) {
    // We can freely presume that width <= cols
    if (item.x + (item.w > 0 ? item.w : 1) > cols) item.x = cols - item.w;
    // NOTE: React/Vue Grid Layout seems to set the items width to the bounded cols
    // this makes no sense, since it would cause the item to take over the entire row
    // if you drag it so far to the left that it's out of bounds it should simply
    // just put it back in bounds and shuffle things around.
    if (item.x < 0) item.x = 0;
  }
}

/**
 * Given a layout, compact it by removing gaps between items (horizontally/vertically).
 *
 * Does not preserve the original layout and will modify it in place.
 */
export function compactLayout(layout: Layout, compactType: CompactType): void {
  if (compactType === CompactType.None) return;

  const compareWith = layout.filter((item) => item.static === true);
  const sorted = layout.slice(0).sort((a, b) => {
    // To handle both types of compaction we could firstly compact it vertically then horizontally
    // but in reality we can handle both inside the same function without repetition, all we have to do
    // is sort them vertically then horizontally so we scan horizontally across each line
    // this will have the same effect

    // For some reason React/Vue GridLayout change the order of sorting based on compaction
    // unaware of any valid reasoning for why the sorting matters, this may result in slightly
    // different end results for compaction around edge cases (unaware of any) that doesn't matter
    // this just compacts, the result is a compacted layout, not a layout to a specification.

    // Check if a is before the other element
    // either vertically or if they are on the same level horizontally
    if (a.y < b.y || (a.y == b.y && a.x < b.x)) {
      return -1;
    } else if (a.y === b.y && a.x === b.x) {
      // this is probably the same item?  Or some sort of conflict
      return 0;
    } else {
      // otherwise a.y > b.y or they are the same but a.x > b.x
      return 1;
    }
  });

  for (const item of sorted) {
    if (!item.static) {
      compactLayoutItem(compareWith, item, compactType);

      // static items are already included in the comparison array
      compareWith.push(item);
    }
  }
}

export function compactLayoutItem(compareWith: Layout, item: LayoutItem, compactType: CompactType) {
  // pre-condition: 'layout' has no collisions
  // post-condition: 'layout' has no collisions

  if ((compactType & CompactType.Vertical) == CompactType.Vertical) {
    // this can't introduce a collision since the 'bottom' of the layout can only have items that were previously to the left of this item
    // and thus if no collisions were existing prior no collisions should be existing currently.
    item.y = Math.min(item.y, findHeightOfLayout(compareWith) ?? item.y);

    // ensure that the item doesn't move before any other items processed if we are preserving visual order
    if ((compactType & CompactType.PreservingVisualOrder) != CompactType.PreservingVisualOrder) {
      // we aren't preserving visual order, so let's feel free to move around items till they vertically fit
      // without collisions with other items (only need to consider those that are processed)
      while (item.y > 0 && !getFirstCollision(compareWith, item)) {
        item.y--;
        // FIXME: this doesn't avoid the half occlusions/visual changes that could occur
        /* i.e.
         *
         * |-A-||--B--|
         *      |-----|
         * |-C-|
         * |---|
         */
        // The above item would cause C to be in a potentially weird place since the visual would be half over another visual
        /* i.e.
         *
         * |-A-||--B--|
         * |-C-||-----|
         * |---|
         */
        // this may be intentional (probably is not) and thus should be behind a flag (like preserving visual order)
        // in which case we should always decrement 'y' by amounts of it's height instead of by 1.
        // For now, our use case of 'preservingVisualOrder' will also cause this to not occur meaning that this is not really an issue.
      }
    }
  }

  if ((compactType & CompactType.Horizontal) == CompactType.Horizontal) {
    // pretty much the same as above, but we can actually be more optimal since we know that
    // both vertical and horizontal layout items *before* this one have been moved.
    // we simply just have to find the 'right' point of the layout at this 'y' coord
    // since we are processing this in order of y then x, we only have to consider the layout that has been sorted up till this point
    // i.e. compareWith.
    let min: number | null = null;
    for (const { x, w } of compareWith.filter((i) => i.y == item.y)) {
      if (!min || x + w > min) min = x + w;
    }

    // if there is nothing on this y level, then we just push it to the start
    if (!min) min = 0;
    item.x = min;
  }
}

export function getFirstCollision(compareWith: Layout, item: LayoutItem): LayoutItem | null {
  return compareWith.find((other) => checkIfLayoutItemsCollide(other, item)) ?? null;
}
